import React, { useEffect, useRef, useState } from "react";
import { Mail, ShieldCheck, RefreshCw } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RESEND_COOLDOWN_S = 60;

/**
 * VerifyEmailSheet — reusable 6-digit email verification flow.
 * Triggers `/api/auth/verify/send` on open, then accepts code → `/auth/verify/confirm`.
 * Refreshes the auth context on success.
 */
export function VerifyEmailSheet({ open, onClose, onVerified }) {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const sentOnceRef = useRef(false);

  // Auto-send a fresh code the first time the sheet opens for this user.
  useEffect(() => {
    if (!open || !user || sentOnceRef.current) return;
    sentOnceRef.current = true;
    sendCode(/* silent */ true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Reset state when reopened for a fresh user
  useEffect(() => {
    if (!open) {
      setCode("");
      setError("");
      setInfo("");
      sentOnceRef.current = false;
    }
  }, [open]);

  const sendCode = async (silent = false) => {
    if (cooldown > 0 || !user) return;
    try {
      setError("");
      await api.post("/auth/verify/send", { email: user.email });
      setCooldown(RESEND_COOLDOWN_S);
      if (!silent) setInfo("Code sent. Check your inbox.");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setError(detail);
    }
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const trimmed = code.replace(/\D/g, "").slice(0, 6);
    if (trimmed.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/verify/confirm", {
        email: user.email,
        code: trimmed,
      });
      // Pull fresh /auth/me so user.email_verified flips true everywhere.
      await refresh();
      if (onVerified) onVerified();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Verify your email">
      <div className="space-y-5" data-testid="verify-email-sheet">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-medium leading-snug">
              We sent a 6-digit code to{" "}
              <span className="font-display">{user?.email}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              It expires in 30 minutes. Verifying unlocks family invites and
              keeps your account safe.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
              Verification code
            </span>
            <input
              data-testid="verify-code-input"
              autoFocus
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="123456"
              className="mt-1 w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[28px] font-mono tracking-[0.4em] text-center"
            />
          </label>

          {error && (
            <p
              data-testid="verify-error"
              className="text-sm text-destructive bg-destructive/10 rounded-lg p-2"
            >
              {error}
            </p>
          )}
          {info && !error && (
            <p
              data-testid="verify-info"
              className="text-sm text-muted-foreground bg-muted/40 rounded-lg p-2"
            >
              {info}
            </p>
          )}

          <button
            data-testid="verify-submit"
            type="submit"
            disabled={busy || code.length !== 6}
            className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap inline-flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <ShieldCheck className="w-4 h-4" />
            {busy ? "Verifying…" : "Verify email"}
          </button>

          <button
            type="button"
            data-testid="verify-resend"
            onClick={() => sendCode(false)}
            disabled={cooldown > 0}
            className="w-full h-11 rounded-2xl border border-border text-[14px] font-medium tap inline-flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <RefreshCw className="w-4 h-4" />
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      </div>
    </BottomSheet>
  );
}
